import { Box, styled } from '@mui/material';
import { useRef, useEffect } from 'react';

const StyledHistoryLayout = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: '#101321B2',
  border: '1.5px solid #6672A6',
  borderRadius: '10px',
  padding: '18px',
  overflowY: 'auto',
  boxSizing: 'border-box',
  maxHeight: '38vh', // 모바일에서 45vh로 제한 (패딩 고려)
  [theme.breakpoints.up('sm')]: {
    paddingLeft: '2.1vw',
    paddingRight: '2.1vw',
    paddingTop: '5vh',
    maxHeight: '100%'
  },
  '&::-webkit-scrollbar': {
    width: '2px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    padding: '10px 0', // 위아래 여백 추가로 가동 범위 제한
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#EBEDF766',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#EBEDF7',
  },
}));

interface HistoryLayoutProps {
  children: React.ReactNode;
}

const HistoryLayout: React.FC<HistoryLayoutProps> = ({ children }) => {
  const layoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (!layoutRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = layoutRef.current;
      const deltaY = event.deltaY;

      // 스크롤이 최상단 또는 최하단에 도달했는지 확인
      const atTop = scrollTop === 0 && deltaY < 0; // 위로 스크롤 시 최상단
      const atBottom = scrollTop + clientHeight >= scrollHeight && deltaY > 0; // 아래로 스크롤 시 최하단

      if (!atTop && !atBottom) {
        // HistoryLayout 내부에서 스크롤 가능하면 상위 이벤트 전파 막기
        event.stopPropagation();
      } else {
        // 부모로 이벤트 전파 허용
        event.preventDefault(); // 모바일에서 튐 방지
      }
      // 최상단/최하단에서는 이벤트 전파 허용 (react-slick이 슬라이드 이동 처리)
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!layoutRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = layoutRef.current;

      // 스크롤 가능 여부 확인
      const canScrollUp = scrollTop > 0;
      const canScrollDown = scrollTop + clientHeight < scrollHeight;

      if (canScrollUp || canScrollDown) {
        // HistoryLayout 내부에서 스크롤 가능하면 상위 이벤트 전파 막기
        event.stopPropagation();
      } else {
        event.preventDefault(); // 터치 이동 시 튐 방지
      }
    };

    const element = layoutRef.current;
    if (element) {
      element.addEventListener('wheel', handleWheel, { passive: false });
      element.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      if (element) {
        element.removeEventListener('wheel', handleWheel);
        element.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, []);

  return <StyledHistoryLayout ref={layoutRef}>{children}</StyledHistoryLayout>;
};

export default HistoryLayout;